/**
 * Isolated profile / CAQH fields so typing does not re-render the full UserSettings tree.
 */
import { forwardRef, useEffect, useImperativeHandle, useState, type MutableRefObject } from 'react';
import { Grid, Group, Radio, Select, TextInput, Textarea, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCalendarEvent } from '@tabler/icons-react';
import { ProfileCheckboxSection } from '../../../components/ProfileCheckboxSection';
import type { ProfileDemographics, User } from '../../users/types/rbac';
import {
  AGE_GROUP_OPTIONS,
  CLINICAL_SPECIALTIES_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  LANGUAGES_OPTIONS,
  LGBTQIA_OPTIONS,
  RACE_ETHNICITY_OPTIONS,
  RELIGION_OPTIONS,
  TREATMENT_MODALITIES_OPTIONS,
  US_STATES,
} from './userSettingsProfileConstants';

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmdToLocalDate(ymd: string): Date {
  const parts = ymd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return new Date(ymd);
  }
  const [y, mo, d] = parts;
  return new Date(y, mo - 1, d);
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function formatPhoneNumber(value: string): string {
  const digits = digitsOnly(value).slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export type DemographicsDraftHandle = { getDemographics: () => ProfileDemographics };

export const DraftProfileDemographics = forwardRef<
  DemographicsDraftHandle,
  { userId: string; initial?: ProfileDemographics; onDirty?: () => void }
>(function DraftProfileDemographics({ userId, initial, onDirty }, ref) {
  const [genderIdentity, setGenderIdentity] = useState(initial?.genderIdentity ?? '');
  const [lgbtqiaIdentities, setLgbtqiaIdentities] = useState(initial?.lgbtqiaIdentities ?? []);
  const [religion, setReligion] = useState(initial?.religion ?? []);
  const [raceEthnicity, setRaceEthnicity] = useState(initial?.raceEthnicity ?? []);
  const [preferredAgeGroups, setPreferredAgeGroups] = useState(initial?.preferredAgeGroups ?? []);
  const [languagesSpoken, setLanguagesSpoken] = useState(initial?.languagesSpoken ?? []);
  const [clinicalSpecialties, setClinicalSpecialties] = useState(initial?.clinicalSpecialties ?? []);

  useEffect(() => {
    setGenderIdentity(initial?.genderIdentity ?? '');
    setLgbtqiaIdentities(initial?.lgbtqiaIdentities ?? []);
    setReligion(initial?.religion ?? []);
    setRaceEthnicity(initial?.raceEthnicity ?? []);
    setPreferredAgeGroups(initial?.preferredAgeGroups ?? []);
    setLanguagesSpoken(initial?.languagesSpoken ?? []);
    setClinicalSpecialties(initial?.clinicalSpecialties ?? []);
  }, [userId, initial]);

  useImperativeHandle(
    ref,
    () => ({
      getDemographics: (): ProfileDemographics => ({
        genderIdentity: genderIdentity || undefined,
        lgbtqiaIdentities: lgbtqiaIdentities.length ? lgbtqiaIdentities : undefined,
        religion: religion.length ? religion : undefined,
        raceEthnicity: raceEthnicity.length ? raceEthnicity : undefined,
        preferredAgeGroups: preferredAgeGroups.length ? preferredAgeGroups : undefined,
        languagesSpoken: languagesSpoken.length ? languagesSpoken : undefined,
        clinicalSpecialties: clinicalSpecialties.length ? clinicalSpecialties : undefined,
      }),
    }),
    [
      genderIdentity,
      lgbtqiaIdentities,
      religion,
      raceEthnicity,
      preferredAgeGroups,
      languagesSpoken,
      clinicalSpecialties,
    ],
  );

  return (
    <>
      <div>
        <Title order={5} mb="xs" c="dark">
          Gender Identity
        </Title>
        <Radio.Group
          value={genderIdentity}
          onChange={(v) => {
            setGenderIdentity(v);
            onDirty?.();
          }}
        >
          <Group gap="lg">
            {GENDER_IDENTITY_OPTIONS.map((opt) => (
              <Radio key={opt} value={opt} label={opt} />
            ))}
          </Group>
        </Radio.Group>
      </div>
      <ProfileCheckboxSection
        label="LGBTQIA+ Identities"
        options={LGBTQIA_OPTIONS}
        columns={3}
        value={lgbtqiaIdentities}
        onChange={(v) => {
          setLgbtqiaIdentities(v);
          onDirty?.();
        }}
      />
      <ProfileCheckboxSection
        label="Religion and Spirituality"
        options={RELIGION_OPTIONS}
        columns={3}
        value={religion}
        onChange={(v) => {
          setReligion(v);
          onDirty?.();
        }}
      />
      <ProfileCheckboxSection
        label="Race and Ethnicity"
        options={RACE_ETHNICITY_OPTIONS}
        columns={3}
        value={raceEthnicity}
        onChange={(v) => {
          setRaceEthnicity(v);
          onDirty?.();
        }}
      />
      <ProfileCheckboxSection
        label="Preferred Patient Age Groups"
        options={AGE_GROUP_OPTIONS}
        columns={3}
        value={preferredAgeGroups}
        onChange={(v) => {
          setPreferredAgeGroups(v);
          onDirty?.();
        }}
      />
      <ProfileCheckboxSection
        label="Languages Spoken"
        options={LANGUAGES_OPTIONS}
        columns={3}
        value={languagesSpoken}
        onChange={(v) => {
          setLanguagesSpoken(v);
          onDirty?.();
        }}
      />
      <ProfileCheckboxSection
        label="Clinical Specialties"
        options={CLINICAL_SPECIALTIES_OPTIONS}
        columns={3}
        value={clinicalSpecialties}
        onChange={(v) => {
          setClinicalSpecialties(v);
          onDirty?.();
        }}
      />
    </>
  );
});

export interface ProviderProfileDraftValues {
  phone: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  languages: string[];
  bio: string;
  pronouns: string;
  genderIdentity: string;
  lgbtqiaIdentity: string;
  religion: string[];
  raceEthnicity: string[];
  ageGroups: string[];
  specialties: string[];
  modalities: string[];
}

export type ProviderProfileDraftInitial = Partial<ProviderProfileDraftValues>;

export type ProviderProfileDraftHandle = { getValues: () => ProviderProfileDraftValues };

export const DraftProviderProfileFields = forwardRef<
  ProviderProfileDraftHandle,
  {
    userId: string;
    initial?: ProviderProfileDraftInitial;
    onDirty?: () => void;
    /** Pronouns are collected here so the parent can render that field elsewhere (e.g. after DOB/NPI). */
    pronounsRef: MutableRefObject<string>;
  }
>(function DraftProviderProfileFields({ userId, initial, onDirty, pronounsRef }, ref) {
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [street, setStreet] = useState(initial?.street ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [zipCode, setZipCode] = useState(initial?.zipCode ?? '');
  const [languages, setLanguages] = useState(initial?.languages ?? []);
  const [bio, setBio] = useState(initial?.bio ?? '');
  const [genderIdentity, setGenderIdentity] = useState(initial?.genderIdentity ?? '');
  const [lgbtqiaIdentity, setLgbtqiaIdentity] = useState(initial?.lgbtqiaIdentity ?? '');
  const [religion, setReligion] = useState(initial?.religion ?? []);
  const [raceEthnicity, setRaceEthnicity] = useState(initial?.raceEthnicity ?? []);
  const [ageGroups, setAgeGroups] = useState(initial?.ageGroups ?? []);
  const [specialties, setSpecialties] = useState(initial?.specialties ?? []);
  const [modalities, setModalities] = useState(initial?.modalities ?? []);

  useEffect(() => {
    setPhone(initial?.phone ?? '');
    setStreet(initial?.street ?? '');
    setCity(initial?.city ?? '');
    setState(initial?.state ?? '');
    setZipCode(initial?.zipCode ?? '');
    setLanguages(initial?.languages ?? []);
    setBio(initial?.bio ?? '');
    setGenderIdentity(initial?.genderIdentity ?? '');
    setLgbtqiaIdentity(initial?.lgbtqiaIdentity ?? '');
    setReligion(initial?.religion ?? []);
    setRaceEthnicity(initial?.raceEthnicity ?? []);
    setAgeGroups(initial?.ageGroups ?? []);
    setSpecialties(initial?.specialties ?? []);
    setModalities(initial?.modalities ?? []);
  }, [userId, initial]);

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({
        phone,
        street,
        city,
        state,
        zipCode,
        languages,
        bio,
        pronouns: pronounsRef.current,
        genderIdentity,
        lgbtqiaIdentity,
        religion,
        raceEthnicity,
        ageGroups,
        specialties,
        modalities,
      }),
    }),
    [
      ageGroups,
      bio,
      city,
      genderIdentity,
      lgbtqiaIdentity,
      languages,
      modalities,
      phone,
      pronounsRef,
      raceEthnicity,
      religion,
      specialties,
      state,
      street,
      zipCode,
    ],
  );

  const dirty = () => onDirty?.();

  return (
    <Grid>
      <Grid.Col span={12}>
        <TextInput
          label="Phone Number"
          placeholder="(555) 555-5555"
          value={phone}
          onChange={(event) => {
            setPhone(formatPhoneNumber(event.currentTarget.value));
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          label="Mailing Address"
          placeholder="Street address"
          value={street}
          onChange={(event) => {
            setStreet(event.currentTarget.value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <TextInput
          label="City"
          placeholder="City"
          value={city}
          onChange={(event) => {
            setCity(event.currentTarget.value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <Select
          label="State"
          placeholder="State"
          data={US_STATES}
          value={state || null}
          onChange={(value) => {
            setState(value ?? '');
            dirty();
          }}
          clearable
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 4 }}>
        <TextInput
          label="Zip Code"
          placeholder="Zip code"
          value={zipCode}
          onChange={(event) => {
            setZipCode(digitsOnly(event.currentTarget.value).slice(0, 5));
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Profile Bio"
          placeholder="Tell clients a little about yourself"
          rows={7}
          value={bio}
          onChange={(event) => {
            setBio(event.currentTarget.value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Languages Spoken"
          options={LANGUAGES_OPTIONS}
          columns={3}
          value={languages}
          onChange={(value) => {
            setLanguages(value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Clinical Specialties"
          options={CLINICAL_SPECIALTIES_OPTIONS}
          columns={3}
          value={specialties}
          onChange={(value) => {
            setSpecialties(value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Treatment Modalities"
          options={TREATMENT_MODALITIES_OPTIONS}
          columns={2}
          value={modalities}
          onChange={(value) => {
            setModalities(value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Preferred Patient Age Groups"
          options={AGE_GROUP_OPTIONS}
          columns={2}
          value={ageGroups}
          onChange={(value) => {
            setAgeGroups(value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <div>
          <Title order={5} mb="xs" c="dark">
            Gender Identity
          </Title>
          <Radio.Group
            value={genderIdentity}
            onChange={(value) => {
              setGenderIdentity(value);
              dirty();
            }}
          >
            <Group gap="lg">
              {GENDER_IDENTITY_OPTIONS.map((option) => (
                <Radio key={option} value={option} label={option} />
              ))}
            </Group>
          </Radio.Group>
        </div>
      </Grid.Col>

      <Grid.Col span={12}>
        <div>
          <Title order={5} mb="xs" c="dark">
            Sexual Identity / LGBTQIA+
          </Title>
          <Radio.Group
            value={lgbtqiaIdentity}
            onChange={(value) => {
              setLgbtqiaIdentity(value);
              dirty();
            }}
          >
            <Group gap="lg">
              {LGBTQIA_OPTIONS.map((option) => (
                <Radio key={option} value={option} label={option} />
              ))}
            </Group>
          </Radio.Group>
        </div>
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Religion and Spirituality"
          options={RELIGION_OPTIONS}
          columns={3}
          value={religion}
          onChange={(value) => {
            setReligion(value);
            dirty();
          }}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <ProfileCheckboxSection
          label="Race and Ethnicity"
          options={RACE_ETHNICITY_OPTIONS}
          columns={3}
          value={raceEthnicity}
          onChange={(value) => {
            setRaceEthnicity(value);
            dirty();
          }}
        />
      </Grid.Col>
    </Grid>
  );
});

export type CaqhDraftHandle = { getCaqhPatch: () => Partial<NonNullable<User['clinical']>> };

export const DraftCaqhFields = forwardRef<
  CaqhDraftHandle,
  { userId: string; initial?: User['clinical']; onDirty?: () => void }
>(function DraftCaqhFields({ userId, initial, onDirty }, ref) {
  const [caqhId, setCaqhId] = useState(initial?.caqhId ?? '');
  const [caqhUsername, setCaqhUsername] = useState(initial?.caqhUsername ?? '');
  const [caqhPassword, setCaqhPassword] = useState(initial?.caqhPassword ?? '');
  const [lastCaqhAttestation, setLastCaqhAttestation] = useState(initial?.lastCaqhAttestation ?? '');

  useEffect(() => {
    setCaqhId(initial?.caqhId ?? '');
    setCaqhUsername(initial?.caqhUsername ?? '');
    setCaqhPassword(initial?.caqhPassword ?? '');
    setLastCaqhAttestation(initial?.lastCaqhAttestation ?? '');
  }, [userId, initial]);

  useImperativeHandle(
    ref,
    () => ({
      getCaqhPatch: () => ({
        caqhId: caqhId || undefined,
        caqhUsername: caqhUsername || undefined,
        caqhPassword: caqhPassword || undefined,
        lastCaqhAttestation: lastCaqhAttestation || undefined,
      }),
    }),
    [caqhId, caqhUsername, caqhPassword, lastCaqhAttestation],
  );

  const dirty = () => onDirty?.();

  return (
    <Grid>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="CAQH ID"
          placeholder="Enter CAQH ID"
          value={caqhId}
          onChange={(e) => {
            setCaqhId(e.currentTarget.value);
            dirty();
          }}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <DateInput
          label="Last CAQH Attestation"
          placeholder="Select a date"
          value={lastCaqhAttestation ? parseYmdToLocalDate(lastCaqhAttestation) : null}
          onChange={(v) => {
            setLastCaqhAttestation(v != null ? (typeof v === 'string' ? v : formatLocalYmd(v)) : '');
            dirty();
          }}
          leftSection={<IconCalendarEvent size={16} />}
          clearable
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="CAQH Username"
          placeholder="Enter username"
          value={caqhUsername}
          onChange={(e) => {
            setCaqhUsername(e.currentTarget.value);
            dirty();
          }}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="CAQH Password"
          placeholder="Enter password"
          type="password"
          value={caqhPassword}
          onChange={(e) => {
            setCaqhPassword(e.currentTarget.value);
            dirty();
          }}
        />
      </Grid.Col>
    </Grid>
  );
});

export type DobZipDraftHandle = { getDateOfBirthYmd: () => string; getZip: () => string };

/** Local DOB + zip state so date/zip edits do not re-render UserSettings. */
export const DraftDobAndZip = forwardRef<
  DobZipDraftHandle,
  { userId: string; initialClinical?: User['clinical']; onDirty?: () => void }
>(function DraftDobAndZip({ userId, initialClinical, onDirty }, ref) {
  const [dob, setDob] = useState(initialClinical?.dateOfBirth ?? '');
  const [zip, setZip] = useState(initialClinical?.zipCode ?? '');

  useEffect(() => {
    setDob(initialClinical?.dateOfBirth ?? '');
    setZip(initialClinical?.zipCode ?? '');
  }, [userId, initialClinical?.dateOfBirth, initialClinical?.zipCode]);

  useImperativeHandle(
    ref,
    () => ({
      getDateOfBirthYmd: () => dob,
      getZip: () => zip,
    }),
    [dob, zip],
  );

  const dirty = () => onDirty?.();

  return (
    <Group grow>
      <DateInput
        label="Date of Birth"
        placeholder="Select a date"
        value={dob ? parseYmdToLocalDate(dob) : null}
        onChange={(v) => {
          setDob(v != null ? (typeof v === 'string' ? v : formatLocalYmd(v)) : '');
          dirty();
        }}
        leftSection={<IconCalendarEvent size={16} />}
        clearable
      />
      <TextInput
        label="Zip/Code"
        placeholder="Zip/Code"
        id="client-profile-postal-code"
        name="client-profile-postal-code"
        // section-* scopes this away from login/email autofill groups; readOnly-until-focus
        // stops Chrome from painting saved email into the field on page load.
        autoComplete="section-client-profile postal-code"
        inputMode="numeric"
        data-1p-ignore
        data-lpignore="true"
        data-bwignore="true"
        data-form-type="other"
        readOnly
        onFocus={(e) => {
          e.currentTarget.removeAttribute('readOnly');
        }}
        value={zip}
        onChange={(e) => {
          setZip(e.currentTarget.value);
          dirty();
        }}
      />
    </Group>
  );
});
